import { Injectable } from '@angular/core';
import { ToastController } from '@ionic/angular';
import { AppConstants, IconUrls } from '../utilities/constant';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root'
})
export class ToastService {
  private readonly roadmapTokenPattern = /(Roadmap|roadmap)/g;
  private readonly legacyToastTranslationKeyMap: Record<string, string> = {
    'role name already exists': 'role_name_already_exists',
    'no results found': 'no_results_found',
    'image size exceeds 100mb': 'image_size_exceeds_100mb',
    'machine\'s created successfully': 'machines_uploaded_successfully',
    'machines created successfully': 'machines_uploaded_successfully',
    'final quote upload successful': 'file_uploaded_successfully',
    'opportunity deleted successfully': 'deleted_successfully',
    'offer is approved successfully': 'Offer Approved',
    'conflict resolved successfully': 'conflict_resolved_successfully',
    'copied successfully': 'copied_successfully',
    'erp commercial data requested': 'erp_commercial_data_requested',
    'failed to add staff to project': 'failed_to_add_staff_to_project',
    'failed to copy hazards': 'failed_to_copy_hazards',
    'failed to reorder': 'failed_to_reorder',
    'final quote uploaded successfully': 'final_quote_uploaded_successfully',
    'hazard deleted successfully': 'hazard_deleted_successfully',
    'hazards copied successfully': 'hazards_copied_successfully',
    'image size exceeds 10mb': 'image_size_exceeds_10mb',
    'log actual hours edited successfully': 'log_actual_hours_edited_successfully',
    'machine\'s deleted successfully': 'machines_deleted_successfully',
    'machines edited succesfully': 'machines_edited_successfully',
    'mark as complete successfully updated': 'mark_as_complete_successfully_updated',
    'no services found': 'no_services_found',
    'not a valid user': 'not_a_valid_user',
    'offer approved successfully': 'Offer Approved',
    'offer is assigned': 'offer_assigned',
    'offer is created successfully': 'offer_created_successfully',
    'offer is rejected': 'offer_rejected',
    'opportunity is created successfully': 'opportunity_created_successfully',
    'please add machine details': 'please_add_machine_details',
    'please check the details': 'please_check_the_details',
    'please choose any machine': 'please_choose_any_machine',
    'please complete all roadmap': 'please_complete_all_roadmap',
    'please resolve all conflicts': 'please_resolve_all_conflicts',
    'please select template': 'please_select_template',
    'please update any role or permissions': 'please_update_role_or_permissions',
    'project edited successfully': 'project_edited_successfully',
    'reordered successfully': 'reordered_successfully',
    'report upload fail': 'report_upload_failed',
    'report upload failed': 'report_upload_failed',
    'report uploaded succesfully': 'report_uploaded_successfully',
    'requested 4 eyes quality check': 'requested_four_eyes_quality_check',
    'roadmap lists are empty': 'roadmap_lists_empty',
    'service successfully saved': 'service_successfully_saved',
    'sorry you do not have the access to view machines': 'no_access_view_machines',
    'you do not have the access to assign staff. please contact admin': 'no_access_assign_staff',
    'you do not have the access to delete assigned staff. please contact admin': 'no_access_delete_assigned_staff',
    'you do not have the access to view offer. please contact admin': 'no_access_view_offer',
    'you do not have the access to view order. please contact admin': 'no_access_view_order',
    'bucket edited successfully': 'bucket_edited_successfully',
    'bucket deleted successfully': 'bucket_deleted_successfully',
    'cannot paste - content already at a4 size limit. please delete some content first': 'cannot_paste_content_at_a4_limit',
    'pasted content was truncated to fit within a4 page size': 'pasted_content_truncated_a4',
    'content exceeds a4 size. please delete some content to continue typing': 'content_exceeds_a4_delete_to_continue',
    'content was truncated to fit within a4 page size': 'content_truncated_to_fit_a4',
    'content exceeds a4 size. only deletion is allowed': 'content_exceeds_a4_only_deletion_allowed',
    'error adding new page. please try again': 'error_adding_new_page_try_again',
    'file name and language cannot be empty': 'file_name_and_language_required',
    'error deleting page. please try again': 'error_deleting_page_try_again',
    'error reordering pages. please try again': 'error_reordering_pages_try_again',
    'category name cannot be empty': 'category_name_cannot_be_empty',
    'please upload image only': 'please_upload_image_only'
  };
  private englishTranslationsPromise: Promise<Record<string, unknown>>;
  private normalizedTranslationKeyCache: Record<string, string>;

  constructor(private toastController: ToastController, 
              public translate: TranslateService) { }

  /**
   * Presents toast
   *
   * @param message
   * @param [duration]
   * @param type
   * @param [position]
   */
  async presentToast(message: string | string[], duration: number = 200000, type: string, position: 'top' | 'bottom' | 'middle' = 'top', fallbackTranslationKey?: string) {
    let snackbarIcon: string;
    let closeIcon: string;
    let cssClas: string;
    let header: string;
    if (Array.isArray(message)) {
    message = message.join('\n');
    }
    message = await this.translateToastMessage(message, fallbackTranslationKey);
    switch (type) {
      case AppConstants.SUCCESS_SNACKBAR:
        snackbarIcon = IconUrls.SUCCESS_ICON;
        closeIcon = IconUrls.SUCCESS_CLOSE;
        cssClas = 'success-toast';
        header = this.translate.instant('success');
        break;
      case AppConstants.WARNING_SNACKBAR:
        snackbarIcon = IconUrls.WARNING_ICON;
        closeIcon = IconUrls.WARNING_CLOSE;
        cssClas = 'warning-toast';
        header = this.translate.instant('warning');
        break;
      case AppConstants.ERROR_SNACKBAR:
        snackbarIcon = IconUrls.ERROR_ICON;
        closeIcon = IconUrls.ERROR_CLOSE;
        cssClas = 'error-toast';
        header = this.translate.instant('error');
        break;
      default:
        break;
    }
    const toast = await this.toastController.create({
      header,
      icon: snackbarIcon,
      message,
      duration,
      position,
      cssClass: 'custom-toast ' + `${cssClas}`,
      buttons: [
        {
          icon: closeIcon,
          role: 'cancel',
        },
      ]
    });
    toast.present();
  }

  private async translateToastMessage(message: string, fallbackTranslationKey?: string): Promise<string> {
    if (!message) {
      return fallbackTranslationKey ? this.translate.instant(fallbackTranslationKey) : message;
    }

    const directTranslation = this.translate.instant(message);
    if (directTranslation !== message) {
      return directTranslation;
    }

    const translationKey = await this.getTranslationKeyForMessage(message);
    if (translationKey) {
      const translatedMessage = this.translate.instant(translationKey);
      if (translatedMessage !== translationKey) {
        return translatedMessage;
      }
    }

    if (!this.roadmapTokenPattern.test(message)) {
      this.roadmapTokenPattern.lastIndex = 0;
      return message;
    }

    this.roadmapTokenPattern.lastIndex = 0;
    const roadmapTokens: string[] = [];
    const tokenizedMessage = message.replace(this.roadmapTokenPattern, (match) => {
      const placeholder = `__ROADMAP_TOKEN_${roadmapTokens.length}__`;
      roadmapTokens.push(match);
      return placeholder;
    });

    const translatedMessage = this.translate.instant(tokenizedMessage);
    const restoredMessage = roadmapTokens.reduce((updatedMessage, roadmapToken, index) => {
      return updatedMessage.replace(`__ROADMAP_TOKEN_${index}__`, roadmapToken);
    }, translatedMessage);

    if (restoredMessage !== tokenizedMessage) {
      return restoredMessage;
    }

    return fallbackTranslationKey ? this.translate.instant(fallbackTranslationKey) : restoredMessage;
  }

  private async getTranslationKeyForMessage(message: string): Promise<string> {
    const normalizedMessage = this.normalizeTranslationMessage(message);
    if (this.legacyToastTranslationKeyMap[normalizedMessage]) {
      return this.legacyToastTranslationKeyMap[normalizedMessage];
    }

    const normalizedTranslationKeyCache = await this.getNormalizedTranslationKeyCache();
    return normalizedTranslationKeyCache[normalizedMessage];
  }

  private async getNormalizedTranslationKeyCache(): Promise<Record<string, string>> {
    if (this.normalizedTranslationKeyCache) {
      return this.normalizedTranslationKeyCache;
    }

    const englishTranslations = await this.getEnglishTranslations();
    const normalizedTranslationKeyCache: Record<string, string> = {};

    this.flattenTranslations(englishTranslations).forEach(([translationKey, translationValue]) => {
      normalizedTranslationKeyCache[this.normalizeTranslationMessage(translationKey)] = translationKey;
      normalizedTranslationKeyCache[this.normalizeTranslationMessage(translationValue)] = translationKey;
    });

    this.normalizedTranslationKeyCache = normalizedTranslationKeyCache;
    return normalizedTranslationKeyCache;
  }

  private async getEnglishTranslations(): Promise<Record<string, unknown>> {
    if (!this.englishTranslationsPromise) {
      this.englishTranslationsPromise = this.translate.getTranslation('en').toPromise();
    }

    return this.englishTranslationsPromise;
  }

  private flattenTranslations(translations: Record<string, unknown>, parentKey: string = ''): Array<[string, string]> {
    return Object.entries(translations).reduce((flattenedTranslations, [translationKey, translationValue]) => {
      const compoundKey = parentKey ? `${parentKey}.${translationKey}` : translationKey;

      if (typeof translationValue === 'string') {
        flattenedTranslations.push([compoundKey, translationValue]);
        return flattenedTranslations;
      }

      if (!translationValue || typeof translationValue !== 'object' || Array.isArray(translationValue)) {
        return flattenedTranslations;
      }

      return flattenedTranslations.concat(this.flattenTranslations(translationValue as Record<string, unknown>, compoundKey));
    }, [] as Array<[string, string]>);
  }

  private normalizeTranslationMessage(message: string): string {
    return message
      .toLowerCase()
      .replace(/[.!?]+$/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  }
}
